import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Divider, 
  List, ListItem, ListItemText, ListItemAvatar, ListItemSecondaryAction,
  Avatar, Chip, TextField, InputAdornment, Badge,
  CircularProgress, Pagination, Tab, Tabs, useTheme, alpha,
  Alert, Snackbar, Button, IconButton, Dialog, DialogTitle, 
  DialogContent, DialogContentText, DialogActions, Tooltip,
  Grid, Card, CardContent, Skeleton, Zoom, Fade, Grow
} from '@mui/material';
import {
  Email as EmailIcon,
  Search as SearchIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Campaign as CampaignIcon,
  Inbox as InboxIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  ChatBubble as ChatBubbleIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { formatDistance, format } from 'date-fns';
import { motion } from 'framer-motion';
import messageService from '../../../services/messageService';
import { useNotification } from '../../../contexts/NotificationContext';

const MemberMessagesPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const notification = useNotification();
  
  // Animation states
  const [showContent, setShowContent] = useState(false);
  
  useEffect(() => {
    // Add animation delay for better UX
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const showLocalNotification = (message, severity = 'info') => {
    if (notification) {
      if (typeof notification.notify === 'function') {
        notification.notify(message, severity);
        return;
      } else if (typeof notification.showNotification === 'function') {
        notification.showNotification(message, severity);
        return;
      } else if (typeof notification.setNotification === 'function') {
        notification.setNotification({ message, severity, open: true });
        return;
      }
    }
    
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tabValue, setTabValue] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    messageId: null
  });
  
  const [refreshing, setRefreshing] = useState(false);
  const [messageStats, setMessageStats] = useState({
    total: 0,
    unread: 0,
    broadcast: 0,
    sent: 0
  });
  
  const fetchInboxMessages = async () => {
    setLoading(true);
    try {
      // Get filter based on current tab, excluding 'sent' tab
      const filter = tabValue === 'sent' ? 'all' : tabValue;
      
      const response = await messageService.getMemberMessages(page, 10, filter);
      if (response.success) {
        setMessages(response.data);
        setTotalPages(response.totalPages || 1);
        
        // Update stats if available in response
        if (response.stats) {
          setMessageStats(prevStats => ({
            ...prevStats,
            total: response.stats.total || 0,
            unread: response.stats.unread || 0,
            broadcast: response.stats.broadcast || 0
          }));
        }
      } else {
        setError(response.error || 'Failed to fetch messages');
        showLocalNotification('Failed to fetch messages', 'error');
      }
    } catch (err) {
      setError('An error occurred while fetching messages');
      showLocalNotification('An error occurred while fetching messages', 'error');
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSentMessages = async () => {
    setLoading(true);
    try {
      const response = await messageService.getMemberSentMessages(page, 10);
      if (response.success) {
        setMessages(response.data);
        setTotalPages(response.totalPages || 1);
        
        // Update sent stats if available
        if (response.stats) {
          setMessageStats(prevStats => ({
            ...prevStats,
            sent: response.stats.sent || 0
          }));
        }
      } else {
        setError(response.error || 'Failed to fetch sent messages');
        showLocalNotification('Failed to fetch sent messages', 'error');
      }
    } catch (err) {
      setError('An error occurred while fetching sent messages');
      showLocalNotification('An error occurred while fetching sent messages', 'error');
      console.error('Error fetching sent messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessageStats = async () => {
    try {
      const response = await messageService.getMemberMessageStats();
      if (response.success) {
        // The response.data contains the actual stats from the API
        setMessageStats({
          total: response.data?.total || 0,
          unread: response.data?.unread || 0,
          broadcast: response.data?.broadcast || 0,
          sent: response.data?.sent || 0
        });
        
        // Log the stats for debugging
        console.log('Message stats updated:', response.data);
      } else {
        console.error('Failed to fetch message stats:', response.error);
      }
    } catch (err) {
      console.error('Error fetching message stats:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      tabValue === 'sent' ? fetchSentMessages() : fetchInboxMessages(),
      fetchMessageStats()
    ]);
    setRefreshing(false);
    showLocalNotification('Messages refreshed', 'success');
  };
  
  useEffect(() => {
    // Reset page when changing tabs
    if (page !== 1) {
      setPage(1);
    } else {
      // Fetch appropriate messages based on tab
      if (tabValue === 'sent') {
        fetchSentMessages();
      } else {
        fetchInboxMessages();
      }
    }
    
    // Also fetch stats
    fetchMessageStats();
  }, [tabValue]);
  
  useEffect(() => {
    // Fetch messages when page changes
    if (tabValue === 'sent') {
      fetchSentMessages();
    } else {
      fetchInboxMessages();
    }
  }, [page]);
  
  useEffect(() => {
    if (searchTerm) {
      const timer = setTimeout(() => {
        const filteredMessages = messages.filter(message => 
          message.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          message.text?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setMessages(filteredMessages);
      }, 300); // Debounce search
      return () => clearTimeout(timer);
    } else {
      // If search is cleared, refetch the messages
      if (tabValue === 'sent') {
        fetchSentMessages();
      } else {
        fetchInboxMessages();
      }
    }
  }, [searchTerm]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleOpenDeleteDialog = (messageId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteDialog({
      open: true,
      messageId
    });
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      messageId: null
    });
  };
  
  const handleDeleteMessage = async () => {
    try {
      const response = await messageService.memberDeleteMessage(deleteDialog.messageId);
      if (response.success) {
        showLocalNotification('Message deleted successfully', 'success');
        // Refresh messages
        if (tabValue === 'sent') {
          fetchSentMessages();
        } else {
          fetchInboxMessages();
        }
        // Also update stats after deletion
        fetchMessageStats();
      } else {
        showLocalNotification(response.error || 'Failed to delete message', 'error');
      }
    } catch (err) {
      showLocalNotification('An error occurred while deleting the message', 'error');
      console.error('Error deleting message:', err);
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // Stats card component with animation
  const StatCard = ({ icon, label, value, color, delay }) => (
    <Grow in={showContent} timeout={1000} style={{ transformOrigin: '0 0 0', transitionDelay: `${delay}ms` }}>
      <Card 
        raised
        sx={{ 
          height: '100%',
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette[color].light, 0.2)} 0%, ${alpha(theme.palette[color].main, 0.4)} 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette[color].main, 0.1)}`,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: theme.shadows[10],
            background: `linear-gradient(135deg, ${alpha(theme.palette[color].light, 0.3)} 0%, ${alpha(theme.palette[color].main, 0.5)} 100%)`,
          }
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette[color].dark }}>
              {value}
            </Typography>
            <Avatar sx={{ bgcolor: theme.palette[color].main, boxShadow: theme.shadows[2] }}>
              {icon}
            </Avatar>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {label}
          </Typography>
        </CardContent>
      </Card>
    </Grow>
  );
  
  // Loading skeleton for message list
  const MessageSkeleton = () => (
    <Box sx={{ width: '100%' }}>
      {[1, 2, 3, 4, 5].map((item) => (
        <Box key={item} sx={{ display: 'flex', p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}` }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={20} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Skeleton variant="text" width="30%" height={16} />
              <Skeleton variant="text" width="20%" height={16} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
  
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header with animation */}
      <Fade in={showContent} timeout={800}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            My Messages
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading || refreshing}
              sx={{ borderRadius: 2 }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ChatBubbleIcon />}
              onClick={() => navigate('/member/messages/compose')}
              sx={{ 
                borderRadius: 2, 
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                }
              }}
            >
              Message Admin
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard 
            icon={<InboxIcon />} 
            label="Total Messages" 
            value={messageStats.total} 
            color="primary"
            delay={100}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard 
            icon={<MarkEmailReadIcon />} 
            label="Unread Messages" 
            value={messageStats.unread} 
            color="error"
            delay={200}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard 
            icon={<CampaignIcon />} 
            label="Broadcasts" 
            value={messageStats.broadcast} 
            color="info"
            delay={300}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard 
            icon={<SendIcon />} 
            label="Sent Messages" 
            value={messageStats.sent} 
            color="success"
            delay={400}
          />
        </Grid>
      </Grid>
      
      {/* Main content */}
      <Zoom in={showContent} timeout={1000}>
        <Paper 
          elevation={3}
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: theme.shadows[3],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          {/* Filters and Search */}
          <Box sx={{ 
            px: 2, 
            pt: 2, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ 
                  '& .MuiTab-root': { 
                    minWidth: 'auto',
                    borderRadius: '20px',
                    mx: 0.5,
                    transition: 'all 0.2s ease-in-out',
                  },
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                <Tab 
                  value="all" 
                  label="All"
                  icon={<InboxIcon />}
                  iconPosition="start"
                />
                <Tab 
                  value="unread" 
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      Unread
                      {messageStats.unread > 0 && (
                        <Chip 
                          label={messageStats.unread} 
                          size="small" 
                          color="error" 
                          sx={{ ml: 1, height: 20, minWidth: 20 }} 
                        />
                      )}
                    </Box>
                  }
                  icon={<MarkEmailReadIcon />}
                  iconPosition="start"
                />
                <Tab 
                  value="broadcast" 
                  label="Broadcasts"
                  icon={<CampaignIcon />}
                  iconPosition="start"
                />
                <Tab 
                  value="sent" 
                  label="Sent"
                  icon={<SendIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>
            
            <TextField
              placeholder="Search messages..."
              size="small"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                  },
                  transition: 'all 0.2s ease-in-out',
                }
              }}
              sx={{ 
                width: { xs: '100%', md: '300px' },
                transition: 'all 0.3s ease-in-out',
                '&:focus-within': {
                  width: { md: '350px' },
                }
              }}
            />
          </Box>
          
          <Divider sx={{ mt: 2 }} />
          
          {/* Message List */}
          {loading ? (
            <MessageSkeleton />
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4, color: 'error.main' }}>
              <Typography>{error}</Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => {
                  if (tabValue === 'sent') {
                    fetchSentMessages();
                  } else {
                    fetchInboxMessages();
                  }
                }}
              >
                Try Again
              </Button>
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              color: 'text.secondary'
            }}>
              <Fade in={true} timeout={1000}>
                <EmailIcon sx={{ fontSize: 60, opacity: 0.5, mb: 2 }} />
              </Fade>
              <Typography variant="h6">No messages found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                {searchTerm 
                  ? `No messages match your search "${searchTerm}"`
                  : tabValue === 'sent'
                    ? "You haven't sent any messages yet."
                    : tabValue === 'unread'
                    ? "You have no unread messages."
                    : tabValue === 'broadcast'
                    ? "No broadcast messages found."
                    : "Your inbox is empty."
                }
              </Typography>
              {searchTerm && (
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => setSearchTerm('')}
                  sx={{ mt: 1 }}
                >
                  Clear Search
                </Button>
              )}
              {tabValue !== 'sent' && tabValue !== 'all' && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setTabValue('all')}
                  sx={{ mt: 1 }}
                >
                  View All Messages
                </Button>
              )}
            </Box>
          ) : (
            <List sx={{ width: '100%', p: 0 }}>
              {messages.map((message, index) => (
                <motion.div
                  key={message._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <ListItem 
                    alignItems="flex-start"
                    component={Link}
                    to={`/member/messages/${message._id}`}
                    sx={{ 
                      transition: 'all 0.2s ease-in-out',
                      borderLeft: !message.isRead && tabValue !== 'sent' ? `4px solid ${theme.palette.error.main}` : 'none',
                      pl: !message.isRead && tabValue !== 'sent' ? 2 : 3,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                      position: 'relative',
                      overflow: 'hidden',
                      bgcolor: !message.isRead && tabValue !== 'sent' ? alpha(theme.palette.error.main, 0.05) : 'transparent',
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '& .MuiListItemSecondaryAction-root': {
                          opacity: 1,
                          transform: 'translateX(0)'
                        }
                      },
                      px: 3,
                      py: 2,
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        badgeContent={!message.isRead && tabValue !== 'sent' ? '!' : null}
                        color="error"
                      >
                        <Avatar sx={{ 
                          bgcolor: message.isBroadcast 
                            ? theme.palette.info.main 
                            : (tabValue === 'sent' 
                              ? theme.palette.success.main 
                              : theme.palette.primary.main),
                          boxShadow: theme.shadows[2]
                        }}>
                          {message.isBroadcast ? <CampaignIcon /> : (tabValue === 'sent' ? <SendIcon /> : <EmailIcon />)}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Typography 
                            component="span" 
                            variant="subtitle1"
                            sx={{ 
                              fontWeight: !message.isRead && tabValue !== 'sent' ? 'bold' : 'normal',
                              color: !message.isRead && tabValue !== 'sent' ? 'text.primary' : 'text.secondary'
                            }}
                          >
                            {message.subject}
                          </Typography>
                          {message.isBroadcast && (
                            <Chip
                              label="Broadcast"
                              size="small"
                              color="info"
                              sx={{ height: 24, borderRadius: 12 }}
                            />
                          )}
                          {!message.isRead && tabValue !== 'sent' && (
                            <Chip
                              label="New"
                              size="small"
                              color="error"
                              sx={{ height: 24, borderRadius: 12 }}
                            />
                          )}
                          {tabValue === 'sent' && (
                            <Chip
                              label="Sent"
                              size="small"
                              color="success"
                              sx={{ height: 24, borderRadius: 12 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography
                            component="span"
                            variant="body2"
                            color={!message.isRead && tabValue !== 'sent' ? 'text.primary' : 'text.secondary'}
                            sx={{ 
                              display: 'inline',
                              fontWeight: !message.isRead && tabValue !== 'sent' ? 500 : 'normal',
                            }}
                          >
                            {message.text.length > 100 
                              ? message.text.substring(0, 100) + '...'
                              : message.text}
                          </Typography>
                          <Box sx={{ 
                            mt: 1, 
                            display: 'flex', 
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Tooltip title={format(new Date(message.createdAt), 'PPP p')}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                                  <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDistance(new Date(message.createdAt), new Date(), { addSuffix: true })}
                                  </Typography>
                                </Box>
                              </Tooltip>
                              
                              <Typography variant="caption" color="text.secondary">
                                {tabValue === 'sent' 
                                  ? 'Message to admin'
                                  : (message.isBroadcast 
                                    ? 'Broadcast message from admin'
                                    : 'Direct message from admin')}
                              </Typography>
                            </Box>
                            
                            {message.replies && message.replies.length > 0 && (
                              <Chip
                                icon={<ChatBubbleIcon sx={{ fontSize: '0.75rem !important' }} />}
                                label={`${message.replies.length} ${message.replies.length === 1 ? 'reply' : 'replies'}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.7rem',
                                  '& .MuiChip-icon': { 
                                    ml: 0.5,
                                    mr: -0.25
                                  }
                                }}
                              />
                            )}
                          </Box>
                        </React.Fragment>
                      }
                    />
                    {tabValue === 'sent' && (
                      <ListItemSecondaryAction sx={{ 
                        opacity: { xs: 1, md: 0 },
                        transform: { xs: 'translateX(0)', md: 'translateX(10px)' },
                        transition: 'all 0.2s ease-in-out',
                      }}>
                        <Tooltip title="Delete Message">
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={(e) => handleOpenDeleteDialog(message._id, e)}
                            color="error"
                            size="small"
                            sx={{ 
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.2),
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                </motion.div>
              ))}
            </List>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{ 
                  '& .MuiPaginationItem-root': { 
                    borderRadius: 1,
                    mx: 0.2,
                    transition: 'all 0.2s ease-in-out',
                  },
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }
                }}
              />
            </Box>
          )}
        </Paper>
      </Zoom>
      
      {/* Delete Message Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[5]
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DeleteIcon color="error" sx={{ mr: 1 }} />
            <Typography variant="h6" component="span">
              Delete Message
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this message? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseDeleteDialog} 
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteMessage} 
            color="error"
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: `linear-gradient(45deg, ${theme.palette.error.main} 30%, ${theme.palette.error.dark} 90%)`,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: theme.shadows[3]
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MemberMessagesPage;